import * as XLSX from 'xlsx';

/**
 * Export data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the file (without extension)
 * @param {String} sheetName - Name of the sheet (optional)
 */
export const exportToExcel = (data, filename = 'export', sheetName = 'Sheet1') => {
  try {
    // Boş data check
    if (!data || data.length === 0) {
      alert('Export ediləcək data yoxdur!');
      return;
    }

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Convert data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);

    // Column widths
    const cols = Object.keys(data[0]).map(() => ({ wch: 20 }));
    worksheet['!cols'] = cols;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const fullFilename = `${filename}_${timestamp}.xlsx`;

    // Write and download
    XLSX.writeFile(workbook, fullFilename);

    console.log(`Excel file exported: ${fullFilename}`);
  } catch (error) {
    console.error('Excel export error:', error);
    alert('Excel export zamanı xəta baş verdi!');
  }
};

/**
 * Export multiple sheets to one Excel file
 * @param {Array} sheets - Array of {data, sheetName}
 * @param {String} filename - Name of the file
 */
export const exportMultipleSheetsToExcel = (sheets, filename = 'export') => {
  try {
    if (!sheets || sheets.length === 0) {
      alert('Export ediləcək data yoxdur!');
      return;
    }

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Add each sheet
    sheets.forEach(({ data, sheetName }) => {
      if (data && data.length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(data);

        // Column widths
        const cols = Object.keys(data[0]).map(() => ({ wch: 20 }));
        worksheet['!cols'] = cols;

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      }
    });

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const fullFilename = `${filename}_${timestamp}.xlsx`;

    // Write and download
    XLSX.writeFile(workbook, fullFilename);

    console.log(`Excel file exported: ${fullFilename}`);
  } catch (error) {
    console.error('Excel export error:', error);
    alert('Excel export zamanı xəta baş verdi!');
  }
};

/**
 * Format data for export (clean up objects)
 * @param {Array} data - Raw data
 * @param {Array} fields - Fields to include {key, label}
 * @returns {Array} Formatted data
 */
export const formatDataForExport = (data, fields) => {
  return data.map((item) => {
    const formatted = {};
    fields.forEach(({ key, label }) => {
      formatted[label] = item[key] || 'N/A';
    });
    return formatted;
  });
};

/**
 * Export users to Excel
 * @param {Array} users - Array of user objects
 */
export const exportUsersToExcel = (users) => {
  const fields = [
    { key: 'full_name', label: 'Ad Soyad' },
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email' },
    { key: 'points', label: 'Xallar' },
    { key: 'total_checkins', label: 'Check-ins' },
    { key: 'is_banned', label: 'Status' },
    { key: 'created_at', label: 'Qoşulma Tarixi' },
  ];

  const formattedData = users.map((user) => ({
    'Ad Soyad': user.full_name || 'N/A',
    Username: user.username || 'N/A',
    Email: user.email || 'N/A',
    Xallar: user.points || 0,
    'Check-ins': user.total_checkins || 0,
    Status: user.is_banned ? 'Ban' : 'Aktiv',
    'Qoşulma Tarixi': new Date(user.created_at).toLocaleDateString('az-AZ'),
  }));

  exportToExcel(formattedData, 'users', 'İstifadəçilər');
};

/**
 * Export businesses to Excel
 * @param {Array} businesses - Array of business objects
 */
export const exportBusinessesToExcel = (businesses) => {
  const formattedData = businesses.map((business) => ({
    Ad: business.name || 'N/A',
    Şəhər: business.city || 'N/A',
    Email: business.email || 'N/A',
    Telefon: business.phone || 'N/A',
    'Check-ins': business.total_checkins || 0,
    Status: business.is_active ? 'Aktiv' : 'Deaktiv',
    'Subscription Bitir': business.subscription_end_date
      ? new Date(business.subscription_end_date).toLocaleDateString('az-AZ')
      : 'N/A',
    'Yaradılma Tarixi': new Date(business.created_at).toLocaleDateString('az-AZ'),
  }));

  exportToExcel(formattedData, 'businesses', 'Business-lər');
};

/**
 * Export fraud logs to Excel
 * @param {Array} frauds - Array of fraud log objects
 */
export const exportFraudLogsToExcel = (frauds) => {
  const formattedData = frauds.map((fraud) => ({
    İstifadəçi: fraud.user_name || 'N/A',
    Business: fraud.business_name || 'N/A',
    'Fraud Type': fraud.fraud_type || 'N/A',
    Severity: fraud.severity || 'N/A',
    Status: fraud.resolved ? 'Həll olundu' : 'Aktiv',
    Tarix: new Date(fraud.created_at).toLocaleDateString('az-AZ'),
  }));

  exportToExcel(formattedData, 'fraud_logs', 'Fraud Loqları');
};

/**
 * Export revenue logs to Excel
 * @param {Array} revenues - Array of revenue log objects
 */
export const exportRevenueLogsToExcel = (revenues) => {
  const formattedData = revenues.map((revenue) => ({
    Business: revenue.businesses?.name || 'N/A',
    Məbləğ: `${parseFloat(revenue.amount).toFixed(2)} ₼`,
    Tip: revenue.type || 'N/A',
    Status: revenue.status || 'N/A',
    'Period Başlanğıc': revenue.period_start
      ? new Date(revenue.period_start).toLocaleDateString('az-AZ')
      : 'N/A',
    'Period Son': revenue.period_end
      ? new Date(revenue.period_end).toLocaleDateString('az-AZ')
      : 'N/A',
    Tarix: new Date(revenue.created_at).toLocaleDateString('az-AZ'),
  }));

  exportToExcel(formattedData, 'revenue_logs', 'Gəlir Loqları');
};

/**
 * Export system logs to Excel
 * @param {Array} logs - Array of system log objects
 */
export const exportSystemLogsToExcel = (logs) => {
  const formattedData = logs.map((log) => ({
    Admin: log.admins?.full_name || 'System',
    Action: log.action || 'N/A',
    'Target Type': log.target_type || 'N/A',
    'IP Address': log.ip_address || 'N/A',
    Tarix: new Date(log.created_at).toLocaleString('az-AZ'),
  }));

  exportToExcel(formattedData, 'system_logs', 'Sistem Loqları');
};

/**
 * Export leaderboards to Excel (multiple sheets)
 * @param {Object} leaderboards - Object with all leaderboard data
 */
export const exportLeaderboardsToExcel = (leaderboards) => {
  const sheets = [];

  // Users by check-ins
  if (leaderboards.topUsers) {
    sheets.push({
      data: leaderboards.topUsers.map((user, index) => ({
        Sıra: index + 1,
        'Ad Soyad': user.full_name,
        Username: user.username,
        'Check-ins': user.checkins,
      })),
      sheetName: 'TOP Userlər (Check-in)',
    });
  }

  // Users by points
  if (leaderboards.topByPoints) {
    sheets.push({
      data: leaderboards.topByPoints.map((user, index) => ({
        Sıra: index + 1,
        'Ad Soyad': user.full_name,
        Username: user.username,
        Xallar: user.points || 0,
      })),
      sheetName: 'TOP Userlər (Xal)',
    });
  }

  // Businesses by check-ins
  if (leaderboards.topBusinesses) {
    sheets.push({
      data: leaderboards.topBusinesses.map((business, index) => ({
        Sıra: index + 1,
        Ad: business.name,
        Şəhər: business.city,
        Ziyarətlər: business.checkins,
      })),
      sheetName: 'TOP Business (Ziyarət)',
    });
  }

  // Businesses by revenue
  if (leaderboards.topByRevenue) {
    sheets.push({
      data: leaderboards.topByRevenue.map((business, index) => ({
        Sıra: index + 1,
        Ad: business.name,
        Şəhər: business.city,
        Gəlir: `${business.revenue.toFixed(2)} ₼`,
      })),
      sheetName: 'TOP Business (Gəlir)',
    });
  }

  exportMultipleSheetsToExcel(sheets, 'leaderboards');
};

export default {
  exportToExcel,
  exportMultipleSheetsToExcel,
  formatDataForExport,
  exportUsersToExcel,
  exportBusinessesToExcel,
  exportFraudLogsToExcel,
  exportRevenueLogsToExcel,
  exportSystemLogsToExcel,
  exportLeaderboardsToExcel,
};